import { useCallback, useEffect, useRef, useState } from 'react'

const INITIAL_META = { page: 1, pages: 1, count: 0 }
const DEFAULT_PER_PAGE = 25

const usePaginatedTable = ({
  fetcher,
  rowsKey,
  filtersKey = '',
  initialPerPage = DEFAULT_PER_PAGE
}) => {
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState(INITIAL_META)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(initialPerPage)
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const versionRef = useRef(0)

  const load = useCallback(async() => {
    const version = ++versionRef.current
    setIsLoading(true)
    try {
      const response = await fetcher({
        page, perPage, sortBy: sortColumn, sortDirection
      })
      if (version !== versionRef.current)
        return
      setRows(response[rowsKey] || [])
      setMeta(response.pagy || INITIAL_META)
    } catch {
      if (version !== versionRef.current)
        return
      setRows([])
      setMeta(INITIAL_META)
    } finally {
      if (version === versionRef.current)
        setIsLoading(false)
    }
  }, [fetcher, rowsKey, page, perPage, sortColumn, sortDirection])

  useEffect(() => {
    setPage(1)
  }, [filtersKey, perPage, sortColumn, sortDirection])

  useEffect(() => { load() }, [load])

  const handleSort = useCallback(column => {
    if (isLoading)
      return
    if (sortColumn !== column) {
      setSortColumn(column)
      setSortDirection('asc')
    } else if (sortDirection === 'asc')
      setSortDirection('desc')
    else {
      setSortColumn(null)
      setSortDirection(null)
    }
  }, [isLoading, sortColumn, sortDirection])

  const sortProps = useCallback(key => ({
    currentSort: sortColumn,
    sortDirection,
    sortKey: key,
    onSort: handleSort
  }), [sortColumn, sortDirection, handleSort])

  return {
    rows,
    isLoading,
    sortProps,
    sortBy: sortColumn,
    sortDirection,
    page,
    perPage,
    total: meta.count,
    lastPage: meta.pages,
    setPage,
    setPerPage
  }
}

export default usePaginatedTable
